/* eslint-disable react-hooks/exhaustive-deps */
import Hls from "hls.js";
import { AnimatePresence, motion } from "motion/react";
import { useDebouncedCallback } from "use-debounce";

import { JSX, MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

import Icon from "@/components/common/Icon/Icon.tsx";

import { cn } from "@/utils/cn.ts";

import "./style.css";

interface MovieBannerPlayerProps {
  src: string;
  videoSrc?: string | null;
  index: number;
  slideNum: number;
  onVideoEnd: () => void;
  loop?: boolean;
  canvasRef?: MutableRefObject<HTMLCanvasElement | null>;
  goToDetail?: () => void;
}

const MovieBannerPlayer = ({
  src,
  videoSrc,
  index,
  slideNum,
  onVideoEnd,
  canvasRef,
  loop = false,
  goToDetail,
}: MovieBannerPlayerProps): JSX.Element => {
  const { t } = useTranslation();
  const { ref: divRef, inView } = useInView({
    threshold: 0.5,
  });
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isVideoShown, setIsVideoShown] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (index === slideNum) {
      timeout = setTimeout(() => setIsVideoShown(true), 1000);
    } else {
      setIsVideoReady(false);
      setIsVideoShown(false);
      setIsMuted(true);
    }

    return () => clearTimeout(timeout);
  }, [index, slideNum]);

  useEffect(() => {
    const progressCircle = document.querySelector(`.progress-circle-${index}`) as SVGCircleElement;

    if (videoRef.current && isVideoShown && videoSrc) {
      const video = videoRef.current;
      video.volume = 1;
      const hls = new Hls();
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, async () => {
        hls.levels.forEach((level, levelIndex) => {
          if (level.name === "480") {
            hls.currentLevel = levelIndex;
          }
        });
        await video.play();
      });

      const circumference = 2 * Math.PI * 15;
      if (progressCircle) {
        progressCircle.style.strokeDasharray = `${circumference} ${circumference}`;
        progressCircle.style.strokeDashoffset = `${circumference}`;
        progressCircle.style.stroke = "#FF4B00";

        video.addEventListener("timeupdate", () => {
          const offset = (1 - video.currentTime / video.duration) * circumference;
          progressCircle.style.strokeDashoffset = offset.toString();
        });
      }
    }

    return () => {
      if (progressCircle) {
        progressCircle.style.strokeDashoffset = "0";
        progressCircle.style.stroke = "transparent";
      }
    };
  }, [index, videoSrc, isVideoShown, slideNum]);

  useEffect(() => {
    if (index === slideNum && videoRef.current && isVideoShown) {
      const video = videoRef.current;
      if (inView) {
        video.play();
      } else {
        video.pause();
      }
    }
  }, [index, isVideoShown, slideNum, inView]);

  const canvas = canvasRef?.current;

  const ctx = canvas?.getContext("2d", { willReadFrequently: true });

  const draw = () => {
    const video = videoRef.current;
    if (!video || !canvas || !ctx) return;

    if (video.readyState < 2 || video.paused || video.ended || !isVideoReady) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    ctx.canvas.style.transform = "scale(1.3)";

    debounce();
  };

  const debounce = useDebouncedCallback(() => requestAnimationFrame(draw), 50);

  useEffect(() => {
    if (!ctx) return;

    requestAnimationFrame(draw);

    return () => {
      ctx.canvas.style.transform = "scale(0)";
    };
  }, [src, isVideoReady, inView]);

  return (
    <div
      ref={divRef}
      className={"relative z-[1] aspect-[1880/764] w-full overflow-hidden rounded-3xl max-md:aspect-[375/240]"}
    >
      <div onClick={goToDetail}>
        <AnimatePresence>
          <motion.img
            key="preview"
            src={src}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-full w-full object-cover object-top"
          />
        </AnimatePresence>
        {index == slideNum && videoSrc && (
          <video
            ref={videoRef}
            muted={isMuted}
            loop={loop}
            playsInline
            autoPlay={isVideoReady}
            onCanPlay={() => {
              setIsVideoReady(true);
            }}
            onEnded={() => {
              setIsVideoReady(false);
              onVideoEnd();
            }}
            className={cn(
              "absolute bottom-0 left-0 right-0 top-0 h-full w-full scale-105 object-cover transition-all",
              {
                "opacity-100": isVideoReady,
              },
            )}
          />
        )}
        <div className="absolute left-0 top-0 h-full w-full bg-[#00000040]"></div>
      </div>
      {index === slideNum && isVideoReady && (
        <button
          type={"button"}
          title={isMuted ? t("sound-on") : t("sound-off")}
          onClick={() => setIsMuted((prev) => !prev)}
          className={
            "button-appear absolute right-[3.625rem] top-8 z-[1] rounded-full border-2  border-on-primary p-2 py-3 max-xl:right-5 max-xl:top-5 max-lg:right-4 max-lg:top-4 max-lg:p-[6px] max-lg:py-[10px]"
          }
        >
          <Icon
            name={isMuted ? "muted" : "unmuted"}
            className={"h-4 w-6 fill-on-primary stroke-on-primary max-lg:h-3 max-lg:w-5"}
          />
        </button>
      )}
    </div>
  );
};

export default MovieBannerPlayer;
